import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * @typedef {object} DraperySwatchRefs
 * @property {HTMLButtonElement} button
 * @property {HTMLElement} status
 */

/**
 * Adds the product's swatch variant to the cart without touching the main
 * product form, whose variant id follows the picker. Fires the theme's
 * CartAddEvent so the cart drawer, cart items and cart bubble update as they
 * do for the main add to cart button.
 * @extends {Component<DraperySwatchRefs>}
 */
class DraperySwatchComponent extends Component {
  requiredRefs = ['button', 'status'];

  async addSwatch() {
    const { button, status } = this.refs;
    if (button.getAttribute('aria-busy') === 'true') return;

    button.setAttribute('aria-busy', 'true');
    status.textContent = '';

    const sectionIds = Array.from(
      document.querySelectorAll('cart-items-component'),
      (element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined)
    ).filter(Boolean);

    try {
      const body = JSON.stringify({
        items: [{ id: Number(this.dataset.variantId), quantity: 1 }],
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      });
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
      const data = await response.json();

      if (data.status) {
        status.textContent = data.description || data.message || this.dataset.errorText || '';
        this.dispatchEvent(new CartErrorEvent(this.id, data.message, data.description, data.errors));
        return;
      }

      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((cartResponse) => cartResponse.json());

      this.dispatchEvent(
        new CartAddEvent(cart, this.id, {
          source: 'drapery-swatch-component',
          itemCount: cart.item_count,
          productId: this.dataset.productId,
          variantId: this.dataset.variantId,
          sections: data.sections,
        })
      );
      status.textContent = this.dataset.addedText ?? '';
    } catch (error) {
      console.error(error);
      status.textContent = this.dataset.errorText ?? '';
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('drapery-swatch-component')) {
  customElements.define('drapery-swatch-component', DraperySwatchComponent);
}
